import { Grid, Box as MuiBox } from '@mui/material';
import { DocumentCard } from '../DocumentCard';
import {
  StyledBox,
  HeadingTypography,
  DescriptionTypography,
} from './DocumentRequestWidget.styles';
import {
  WidgetWrapper,
  type IStylesOverrides,
} from '@orcha/micro-library-core-ui';
import type { IAppDetailsAppData } from '../common-types';

export const DOCUMENT_REQUEST_WIDGET_DEFAULT_PADDING = 0;

type IDocumentRequestConfig = {
  title?: string;
  body?: string;
  styleOverrides?: IStylesOverrides;
  cards: IDocumentRequestCard[];
  primaryButton?: { text?: string; link?: string; isExternalLink?: boolean };
};

type IDocumentRequestCard = {
  title?: string;
  link?: string;
  isExternalLink?: boolean;
  primaryButton?: { text?: string; link?: string; isExternalLink?: boolean };
};

type IDocumentRequestUser = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

export type DocumentRequestWidgetProps = {
  app: IAppDetailsAppData;
  configuration?: IDocumentRequestConfig;
  user?: IDocumentRequestUser;
};

/**
 * DocumentRequestWidget component from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param {DocumentRequestWidgetProps} props - The props for the DocumentRequestWidget component
 * @returns {ReactElement} The DocumentRequestWidget component
 */

export const DocumentRequestWidget = ({
  app,
  configuration,
  user,
}: DocumentRequestWidgetProps) => {
  const getDistinctPlatformAccreditation = () => {
    const accreditations =
      app?.platforms?.flatMap((platform) => platform.accreditations) || [];
    const distinctAccreditations = [...new Set(accreditations)];
    return distinctAccreditations.map((p) => p?.name).join(';');
  };

  const { firstName, lastName, email } = (user as IDocumentRequestUser) || {};
  const { styleOverrides, title, body, cards } =
    (configuration as IDocumentRequestConfig) || {};

  const getAppQueryParams = () => {
    const params = new URLSearchParams({
      appName: app?.name,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      appId: app?.appId,
      accreditations: getDistinctPlatformAccreditation(),
    });

    return params.toString();
  };

  return (
    <WidgetWrapper
      styleOverrides={styleOverrides}
      testId="document-request-widget-container"
    >
      <StyledBox styleOverrides={styleOverrides}>
        <HeadingTypography variant="h6">{title ?? ''}</HeadingTypography>
        <DescriptionTypography variant="body2">
          {body ?? ''}
        </DescriptionTypography>
        <Grid container spacing={3}>
          {cards?.map((card) => {
            return (
              <Grid
                key={`${card.title}_${card.link}`}
                item
                xs={12}
                sm={6}
                md={4}
              >
                <MuiBox
                  sx={{
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}
                >
                  <DocumentCard
                    title={card.title}
                    primaryButton={card.primaryButton}
                    cardAction={{
                      link: card?.link,
                      isExternalLink: card?.isExternalLink,
                    }}
                    cardOptions={{
                      isClickable: false,
                      hasOutline: true,
                    }}
                    queryParams={getAppQueryParams()}
                  />
                </MuiBox>
              </Grid>
            );
          })}
        </Grid>
      </StyledBox>
    </WidgetWrapper>
  );
};
